import { SkyButton, SkyMessageBar } from "@skypointcloud/skypoint-ui";
import type { PermissionKey } from "@shared/permissions";
import type { MeInstance } from "@shared/types";

/**
 * A run link that names another instance (contract-notif R12, D9).
 *
 * A notification's link is `/runs/{runId}?instance={instanceId}`. The server reads the instance
 * only from the request header, never from the URL, so a link cannot switch instances on its own
 * and must not: this says which instance the run belongs to and switches only when the reader
 * presses "Switch". A reader who cannot open that instance is told so, and nothing is fetched
 * under the wrong instance in the meantime.
 */
export type RunInstanceLink =
  | { kind: "none" }
  /** A link names an instance, and which one is selected is not known yet (`/me` loading). */
  | { kind: "unresolved" }
  | { kind: "same" }
  | { kind: "other"; instance: MeInstance }
  | { kind: "no_access" };

/**
 * What a run page does with the `?instance=` its address carries. The MCP approve and setup links
 * (ADR 0009, release 8B) ask the same question with the permission their page needs.
 */
export function runInstanceLink(
  pendingInstanceId: string | null,
  selectedInstanceId: string | null,
  instances: readonly MeInstance[],
  permission: PermissionKey = "runs.read",
): RunInstanceLink {
  if (!pendingInstanceId) return { kind: "none" };
  if (!selectedInstanceId) return { kind: "unresolved" };
  if (pendingInstanceId === selectedInstanceId.toLowerCase()) {
    return { kind: "same" };
  }
  const target = instances.find(
    (instance) =>
      instance.id.toLowerCase() === pendingInstanceId && instance.permissions.includes(permission),
  );
  return target ? { kind: "other", instance: target } : { kind: "no_access" };
}

/** The address with its `instance` parameter dropped and every other parameter kept. */
export function withoutInstanceParam(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  params.delete("instance");
  const rest = params.toString();
  return rest ? `${pathname}?${rest}` : pathname;
}

export function RunInstanceSwitchNotice({
  link,
  onSwitch,
}: {
  link: Extract<RunInstanceLink, { kind: "other" | "no_access" }>;
  onSwitch: (instanceId: string) => void;
}) {
  if (link.kind === "no_access") {
    return (
      <SkyMessageBar
        intent="warning"
        layout="multiline"
        testId="run-instance-switch-notice"
        message="You do not have access to the instance this run belongs to."
      />
    );
  }
  const name = link.instance.name;
  return (
    <SkyMessageBar
      intent="info"
      layout="multiline"
      testId="run-instance-switch-notice"
      message={`This run belongs to ${name}. Switch to ${name} to open it.`}
      actions={
        <SkyButton
          appearance="secondary"
          size="small"
          testId="run-instance-switch"
          onClick={() => onSwitch(link.instance.id)}
        >
          Switch
        </SkyButton>
      }
    />
  );
}
